import { useId } from "react";
import { Link2 } from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { InboundStatusMappingEditor } from "@/components/itsm-config/mapping-editors";
import { ScopeReveal } from "@/components/itsm-config/scope-reveal";
import { FreshserviceGroupFilter } from "@/components/itsm-config/freshservice-group-filter";
import { useGeneralMappingSave } from "@/hooks/use-general-mapping-save";
import {
  EMPTY_MATCH,
  findRule,
  hasInboundConfig,
  hasJiraInboundSettings,
  isEmptyMatch,
  isLinkedOnly,
  titleRegexError,
} from "@/lib/itsm-config/general-mapping";
import {
  HARMONY_STATUS_LABELS,
  INBOUND_FIELDS,
  INBOUND_HARMONY_STATUSES,
  PROVIDER_LABELS,
  type GeneralMappingRule,
  type GeneralMatch,
  type InboundConfig,
  type ProviderConfig,
  type ItsmProviderFamily,
} from "@/lib/types/itsm-config";
import { cn } from "@/lib/utils";

interface Props {
  tenantId: string;
  credentialId: string;
  provider: string;
  family: ItsmProviderFamily;
  providerConfig: ProviderConfig;
}

/**
 * Card 4 — inbound (pull) settings: an integration-wide linked-only mode, then
 * watch on/off + since-date per scope. For Jira in linked-only mode, each
 * scope also picks which fields to import, reopen, shadow mode and an inbound
 * status table.
 */
export function InboundSection({
  tenantId,
  credentialId,
  provider,
  family,
  providerConfig,
}: Props) {
  const rules = providerConfig.general_mapping ?? [];
  const save = useGeneralMappingSave();
  // eslint-disable-next-line security/detect-object-injection -- provider is a fixed union
  const providerLabel = PROVIDER_LABELS[provider] ?? provider;
  const isJira = family === "jira";
  const supportsLinkedOnly = family === "servicenow" || isJira;
  const linkedOnly = isLinkedOnly(rules);
  const blockLinkedOnly = !supportsLinkedOnly && !linkedOnly;
  const linkedOnlyTitleId = useId();
  const linkedOnlyNoteId = useId();
  const changeSyncNoteId = useId();

  const hasField = (r: GeneralMappingRule) =>
    hasInboundConfig(r.inbound) || r.outbound_sync != null;

  // Draft updates are synchronous, so we read the current value straight from
  // the draft rules and merge each change onto it.
  const setInbound = (match: GeneralMatch, patch: Partial<InboundConfig>) => {
    const existing = findRule(rules, match)?.inbound;
    const merged: InboundConfig = { ...(existing ?? {}), ...patch };
    // Jira refuses a linked-only inbound block that names no fields, so a
    // block created while linked-only is on starts from status-only.
    if (isJira && linkedOnly && existing == null && !merged.fields?.length) {
      merged.fields = ["status"];
    }
    save(match, { inbound: hasInboundConfig(merged) ? merged : null });
  };

  const setOutbound = (match: GeneralMatch, enabled: boolean) => {
    save(match, { outbound_sync: enabled ? true : null });
  };

  // Linked-only is stored on the tenant-wide default rule. The backend honours
  // it on any rule, so turning it off clears it everywhere, or a stray scoped
  // value would keep the mode on with no switch showing it.
  // Jira rejects linked-only while any inbound rule names no fields to
  // import, so turning it on starts every such rule from status-only.
  const setLinkedOnly = (enabled: boolean) => {
    if (enabled) {
      const needsFields = (inbound: InboundConfig | null | undefined) =>
        isJira && !inbound?.fields?.length;
      setInbound(EMPTY_MATCH, {
        linked_only: true,
        ...(needsFields(findRule(rules, EMPTY_MATCH)?.inbound)
          ? { fields: ["status"] }
          : {}),
      });
      for (const rule of rules) {
        if (
          !isEmptyMatch(rule.match) &&
          rule.inbound != null &&
          needsFields(rule.inbound)
        ) {
          setInbound(rule.match, { fields: ["status"] });
        }
      }
      return;
    }
    for (const rule of rules) {
      if (rule.inbound?.linked_only != null) {
        setInbound(rule.match, { linked_only: null });
      }
    }
  };

  const description = !linkedOnly
    ? `Pull existing ${providerLabel} tickets into Harmony. Turn on watching, ` +
      "and optionally only import tickets created after a date or whose " +
      "title matches a pattern."
    : isJira
      ? "Linked-only: Harmony pulls updates only for Jira issues it exported " +
        "or that are linked to a Harmony ticket; it never imports new issues."
      : supportsLinkedOnly
        ? `Linked-only: Harmony imports only updates to ${providerLabel} ` +
          "records it already syncs, plus their child tasks. Nothing else is " +
          "imported."
      : `Importing is paused: linked-only is on, which ${providerLabel} ` +
        "does not support.";

  const linkedOnlyNote = isJira
    ? "Replaces the sweep for this whole integration. Harmony pulls updates " +
      "only for Jira issues it exported or that are linked to a Harmony " +
      "ticket; it never imports new issues. While on, the watch and " +
      "import-since settings below are ignored, and each scope chooses " +
      "which fields an update may change."
    : supportsLinkedOnly
      ? "Replaces the sweep for this whole integration. Harmony imports only " +
        "updates to records it already syncs, plus child tasks under them " +
        "(e.g. an sc_task under an RITM Harmony ordered). While on, the " +
        "watch and import-since settings below are ignored, and manual ITSM " +
        "import jobs for this integration import nothing. Turning it off " +
        "resumes the sweep from now; records changed meanwhile are not " +
        "backfilled."
      : linkedOnly
        ? `${providerLabel} does not support linked-only import, so while it ` +
          "is on every import pass is skipped. Turn it off to resume importing."
        : `${providerLabel} does not support linked-only import.`;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Importing tickets (inbound)</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {family === "freshservice" && (
          <FreshserviceGroupFilter
            tenantId={tenantId}
            credentialId={credentialId}
            groupIds={providerConfig.inbound_group_ids}
          />
        )}
        <div
          className={cn(
            "flex items-start gap-3 rounded-md border px-4 py-3",
            linkedOnly && "border-amber-500/40 bg-amber-500/10",
          )}
        >
          <Link2
            className={cn(
              "mt-0.5 h-4 w-4 shrink-0",
              linkedOnly ? "text-amber-600" : "text-muted-foreground",
            )}
          />
          <div
            className={cn(
              "flex-1 space-y-0.5",
              !supportsLinkedOnly && !linkedOnly && "text-muted-foreground",
            )}
          >
            <p id={linkedOnlyTitleId} className="text-sm font-medium">
              Import linked records only
              {!supportsLinkedOnly && (
                <span className="text-muted-foreground ml-2 text-xs font-normal">
                  ServiceNow and Jira only
                </span>
              )}
            </p>
            <p id={linkedOnlyNoteId} className="text-muted-foreground text-sm">
              {linkedOnlyNote}
            </p>
          </div>
          <Switch
            checked={linkedOnly}
            aria-labelledby={linkedOnlyTitleId}
            aria-describedby={linkedOnlyNoteId}
            // Unsupported providers can only turn a stray value off. The
            // switch stays focusable (aria-disabled, not disabled) so focus
            // isn't dropped the moment it is switched off.
            aria-disabled={blockLinkedOnly || undefined}
            className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            onCheckedChange={(v) => {
              if (v && !supportsLinkedOnly) return;
              setLinkedOnly(v);
            }}
          />
        </div>

        <div id={changeSyncNoteId} className="text-muted-foreground space-y-1 text-sm">
          <p>
            Import changes: for tenants with change management (ITIL), imports
            {" "}{providerLabel} changes as read-only change records on the
            change calendar. With ITIL on and this off, changes are not
            imported at all. Without ITIL it has no effect.
          </p>
          <p>
            A scope&apos;s own settings replace the default rule&apos;s
            entirely, so a scope this switch creates starts unwatched
            {linkedOnly
              ? " (watching is ignored under linked-only)."
              : ": nothing in it imports until you also turn on watching."}
          </p>
        </div>

        <ScopeReveal
          family={family}
          tenantId={tenantId}
          credentialId={credentialId}
          rules={rules}
          hasField={hasField}
          defaultNote="Applies to all projects."
          onClearScope={(match) => { save(match, { inbound: null, outbound_sync: null }); }}
          renderEditor={(match) => {
            const rule = findRule(rules, match);
            const inbound = rule?.inbound ?? {};
            return (
              <div className="space-y-4">
                <div className="flex flex-wrap items-end gap-6">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={inbound.watch ?? false}
                      disabled={linkedOnly}
                      aria-describedby={linkedOnly ? linkedOnlyNoteId : undefined}
                      onCheckedChange={(v) => { setInbound(match, { watch: v }); }}
                    />
                    <Label className="text-sm">Watch this scope</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={inbound.change_sync ?? false}
                      aria-label="Import changes (read-only)"
                      aria-describedby={changeSyncNoteId}
                      onCheckedChange={(v) => { setInbound(match, { change_sync: v ? true : null }); }}
                    />
                    <Label className="text-sm">Import changes (read-only)</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={rule?.outbound_sync ?? false}
                      onCheckedChange={(v) => { setOutbound(match, v); }}
                    />
                    <Label className="text-sm">Sync updates outbound</Label>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Import since (optional)</Label>
                    <Input
                      type="date"
                      value={(inbound.since_date ?? "").slice(0, 10)}
                      disabled={linkedOnly}
                      aria-describedby={linkedOnly ? linkedOnlyNoteId : undefined}
                      onChange={(e) => { setInbound(match, { since_date: e.target.value || null }); }}
                      className="h-8 w-[200px]"
                    />
                  </div>
                </div>
                <TitleRegexInput
                  value={inbound.title_regex}
                  // Ignored under linked-only, but a stored value stays
                  // editable so it can be cleared.
                  disabled={linkedOnly && !inbound.title_regex}
                  linkedOnlyNoteId={linkedOnly ? linkedOnlyNoteId : undefined}
                  onChange={(v) => { setInbound(match, { title_regex: v }); }}
                />
                {/* Also shown while linked-only is off if a rule still holds
                    these keys, so leftovers stay visible and clearable. */}
                {isJira &&
                  (linkedOnly || hasJiraInboundSettings(rule?.inbound)) && (
                  <JiraLinkedOnlyEditor
                    inbound={inbound}
                    hasInboundBlock={rule?.inbound != null}
                    linkedOnly={linkedOnly}
                    onChange={(patch) => { setInbound(match, patch); }}
                  />
                )}
              </div>
            );
          }}
        />
      </CardContent>
    </Card>
  );
}

/**
 * Optional title filter: a watched scope imports only tickets whose title the
 * pattern matches. Checked as it is typed; the backend validates it on Save.
 */
function TitleRegexInput({
  value,
  disabled,
  linkedOnlyNoteId,
  onChange,
}: {
  value: string | null | undefined;
  disabled: boolean;
  linkedOnlyNoteId: string | undefined;
  onChange: (value: string | null) => void;
}) {
  const inputId = useId();
  const noteId = useId();
  const errorId = useId();
  const error = titleRegexError(value);
  return (
    <div className="space-y-1">
      <Label htmlFor={inputId} className="text-xs">
        Only import titles matching (optional)
      </Label>
      <Input
        id={inputId}
        value={value ?? ""}
        disabled={disabled}
        placeholder="e.g. ^\[Ops\] or (?i)vpn"
        spellCheck={false}
        aria-invalid={error != null || undefined}
        aria-describedby={[errorId, noteId, linkedOnlyNoteId]
          .filter(Boolean)
          .join(" ")}
        onChange={(e) => {
          onChange(e.target.value || null);
        }}
        className="h-8 max-w-md font-mono"
      />
      <p id={errorId} role="status" className="text-destructive text-xs">
        {error ?? ""}
      </p>
      <p id={noteId} className="text-muted-foreground text-xs">
        RE2 regular expression (Python syntax, without lookarounds or
        backreferences), matched anywhere in the title unless anchored with ^
        or $. Case-sensitive; start with (?i) to ignore case. \w, \d and \b
        are ASCII-only; use \pL for any letter. Applies to new tickets only;
        tickets already synced keep syncing. Empty imports every title.
      </p>
    </div>
  );
}

/**
 * Per-scope settings for Jira linked-only import: which fields an update may
 * write, reopen, shadow mode and the inbound status table.
 */
function JiraLinkedOnlyEditor({
  inbound,
  hasInboundBlock,
  linkedOnly,
  onChange,
}: {
  inbound: InboundConfig;
  hasInboundBlock: boolean;
  linkedOnly: boolean;
  onChange: (patch: Partial<InboundConfig>) => void;
}) {
  const fieldsLabelId = useId();
  const fieldsNoteId = useId();
  const statusTitleId = useId();
  const statusNoteId = useId();
  const reopenId = useId();
  const dryRunId = useId();
  const fields = inbound.fields ?? [];
  // Mirrors the save check: only a rule with an inbound block must name its
  // fields.
  const missingFields = linkedOnly && hasInboundBlock && fields.length === 0;

  const toggleField = (name: string, on: boolean) => {
    const rest = fields.filter((f) => f !== name);
    const next = on ? [...rest, name] : rest;
    onChange({ fields: next.length ? next : null });
  };

  return (
    <div className="space-y-4 rounded-md border px-4 py-3">
      <div className="space-y-1.5">
        <p id={fieldsLabelId} className="text-sm font-medium">
          Fields to import
        </p>
        <div
          role="group"
          aria-labelledby={fieldsLabelId}
          aria-describedby={missingFields ? fieldsNoteId : undefined}
          className="flex flex-wrap gap-4"
        >
          {INBOUND_FIELDS.map((f) => {
            const id = `${fieldsLabelId}-${f.value}`;
            return (
              <div key={f.value} className="flex items-center gap-2">
                <Checkbox
                  id={id}
                  checked={fields.includes(f.value)}
                  onCheckedChange={(v) => { toggleField(f.value, v === true); }}
                />
                <Label htmlFor={id} className="text-sm font-normal">
                  {f.label}
                </Label>
              </div>
            );
          })}
        </div>
        <p id={fieldsNoteId} role="status" className="text-destructive text-xs">
          {missingFields
            ? "Pick at least one field. Saving is rejected while linked-only " +
              "is on and this scope imports no fields."
            : ""}
        </p>
      </div>

      <div className="flex flex-wrap gap-6">
        <div className="flex items-center gap-2">
          <Switch
            id={reopenId}
            checked={inbound.reopen ?? false}
            aria-describedby={`${reopenId}-note`}
            onCheckedChange={(v) => { onChange({ reopen: v }); }}
          />
          <Label htmlFor={reopenId} className="text-sm">
            Reopen resolved tickets
          </Label>
          <span id={`${reopenId}-note`} className="text-muted-foreground text-xs">
            Also closed and canceled ones.
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id={dryRunId}
            checked={inbound.dry_run ?? false}
            onCheckedChange={(v) => { onChange({ dry_run: v }); }}
          />
          <Label htmlFor={dryRunId} className="text-sm">
            Shadow mode (log only, write nothing)
          </Label>
        </div>
      </div>

      <div
        role="group"
        aria-labelledby={statusTitleId}
        aria-describedby={statusNoteId}
        className="space-y-1.5"
      >
        <p id={statusTitleId} className="text-sm font-medium">
          Inbound status translation
        </p>
        <p id={statusNoteId} className="text-muted-foreground text-xs">
          When set, only the Jira statuses listed here change the Harmony
          status; any other status is ignored. Names are matched ignoring
          case, with spaces, "-" and "/" read as "_".
        </p>
        <InboundStatusMappingEditor
          value={inbound.status_mapping ?? {}}
          onChange={(m) => {
            onChange({ status_mapping: Object.keys(m).length ? m : null });
          }}
          harmonyOptions={INBOUND_HARMONY_STATUSES}
          harmonyLabels={HARMONY_STATUS_LABELS}
          providerLabel="Jira"
        />
      </div>
    </div>
  );
}
